<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast as sonnerToast } from 'vue-sonner';

// Custom error toast rendered via `toast.custom(...)` (vue-sonner 2.0.9).
// Custom toasts are rendered with `data-styled="false"`, so vue-sonner's
// rich-colors styling does NOT apply — this component owns its full visual.
// We mimic the error look with the n-ruby palette and expose a discreet
// copy icon-button (with tooltip) instead of the bulky text action button.
const props = defineProps({
  message: {
    type: String,
    required: true,
  },
  // Toast id minted by helper/toast.js so we can dismiss this exact toast.
  toastId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();

const copied = ref(false);

const copy = async () => {
  await navigator.clipboard.writeText(props.message);
  copied.value = true;
  setTimeout(() => {
    copied.value = false;
  }, 2000);
};

const close = () => {
  sonnerToast.dismiss(props.toastId);
};
</script>

<template>
  <div
    class="flex w-full items-start gap-2 rounded-lg border border-n-ruby-6 bg-n-ruby-3 px-4 py-3 text-n-ruby-11"
  >
    <span class="i-lucide-circle-alert mt-0.5 size-4 shrink-0" />
    <p class="m-0 flex-1 break-words text-sm leading-snug text-n-ruby-12">
      {{ message }}
    </p>
    <div class="-mr-1 -mt-0.5 flex shrink-0 items-center gap-1">
      <button
        v-tooltip.top="{
          content: t('GENERAL.COPY'),
          theme: 'tooltip-on-toast',
        }"
        type="button"
        class="flex size-7 shrink-0 items-center justify-center rounded-md text-n-ruby-11 transition-colors hover:bg-n-ruby-4 hover:text-n-ruby-12"
        :aria-label="t('GENERAL.COPY')"
        @click="copy"
      >
        <span
          :class="copied ? 'i-lucide-check' : 'i-lucide-copy'"
          class="size-4"
        />
      </button>
      <button
        v-tooltip.top="{
          content: t('GENERAL.CLOSE'),
          theme: 'tooltip-on-toast',
        }"
        type="button"
        class="flex size-7 shrink-0 items-center justify-center rounded-md text-n-ruby-11 transition-colors hover:bg-n-ruby-4 hover:text-n-ruby-12"
        :aria-label="t('GENERAL.CLOSE')"
        @click="close"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>
  </div>
</template>
